
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import './styles/globals.css';
import Footer from './components/Footer';

// Lazy load components for better performance
const IRCTCCalculator = lazy(() => import('./components/IRCTCCalculator.js'));
const ComprehensiveLoanCalculator = lazy(() => import('./components/EmiCalculator.js'));
const SIPCalculator = lazy(() => import('./components/SIPCalculator.js'));
const GSTCalculator = lazy(() => import('./components/GSTCalculator.js'));
const IncomeTaxCalculator = lazy(() => import('./components/IncomeTaxCalculator.js'));
const SalaryCalculator = lazy(() => import('./components/SalaryCalculator.js'));
const AgeCalculator = lazy(() => import('./components/AgeCalculator.js'));
const JobOfferWorkflow = lazy(() => import('./components/JobOfferWorkflow.js'));
const HomeLoanReadinessWorkflow = lazy(() => import('./components/HomeLoanReadinessWorkflow.js'));
const MainLandingPage = lazy(() => import('./components/Main.js'));
const JsonTools = lazy(() => import('./components/JsonTools.js'));
const IndiaCalculatorsHub = lazy(() => import('./components/IndiaCalculatorsHub.js'));
const USCalculatorsHub = lazy(() => import('./components/USCalculatorsHub.js'));
const EUCalculatorsHub = lazy(() => import('./components/EUCalculatorsHub.js'));
const USMortgageCalculator = lazy(() => import('./components/USMortgageCalculator.js'));
const USRefinanceCalculator = lazy(() => import('./components/USRefinanceCalculator.js'));
const USAutoLoanCalculator = lazy(() => import('./components/USAutoLoanCalculator.js'));
const US401kCalculator = lazy(() => import('./components/US401kCalculator.js'));
const USSavingsCDCalculator = lazy(() => import('./components/USSavingsCDCalculator.js'));
const USCreditCardPayoffCalculator = lazy(() => import('./components/USCreditCardPayoffCalculator.js'));

// European Calculators
const UKIncomeTaxCalculator = lazy(() => import('./components/UKIncomeTaxCalculator.js'));
const VATCalculator = lazy(() => import('./components/VATCalculator.js'));
const EuropeanSalaryCalculator = lazy(() => import('./components/EuropeanSalaryCalculator.js'));
const GermanySalaryCalculator = lazy(() => import('./components/GermanySalaryCalculator.js'));
const FranceSalaryCalculator = lazy(() => import('./components/FranceSalaryCalculator.js'));
const NetherlandsSalaryCalculator = lazy(() => import('./components/NetherlandsSalaryCalculator.js'));

// UK Rail Calculator
const UKRailCalculator = lazy(() => import('./components/UKRailCalculator.js'));

// Credit Card Analyzer
const CreditCardAnalyzer = lazy(() => import('./components/CreditCardAnalyzer.js'));
const PPFCalculator = lazy(() => import('./components/PPFCalculator.js'));
const CreditCardTrapCalculator = lazy(() => import('./components/CreditCardTrapCalculator.js'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage.js'));

// Component to conditionally render Footer
const ConditionalFooter = () => {
  const location = useLocation();
  // Don't show comprehensive disclaimer footer on main page
  if (location.pathname === '/') {
    return null;
  }
  return <Footer />;
};

// Lazy-route loading fallback (visible more often in production due chunk fetch latency).
const LoadingSpinner = ({ isDarkMode }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: isDarkMode
      ? 'radial-gradient(circle at top right, rgba(15, 118, 110, 0.22), transparent 46%), linear-gradient(135deg, #0b1726 0%, #15253a 100%)'
      : 'radial-gradient(circle at top right, rgba(180, 83, 9, 0.14), transparent 44%), linear-gradient(135deg, #f6f4ef 0%, #e7edf4 100%)',
    padding: '1.2rem'
  }}>
    <style>{`
      @keyframes upamanSpinner {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes upamanPulse {
        0%, 100% { transform: scale(1); opacity: 0.95; }
        50% { transform: scale(1.04); opacity: 1; }
      }
    `}</style>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.8rem',
      borderRadius: '1rem',
      border: `1px solid ${isDarkMode ? '#2f455e' : '#dbe2eb'}`,
      background: isDarkMode ? 'rgba(20, 35, 53, 0.86)' : 'rgba(255, 255, 255, 0.9)',
      boxShadow: isDarkMode
        ? '0 12px 28px rgba(0, 0, 0, 0.45)'
        : '0 12px 26px rgba(9, 30, 66, 0.2)',
      padding: '1.1rem 1.25rem',
      minWidth: '220px'
    }}>
      <img
        src="/upaman-elephant-logo.svg"
        alt="Upaman"
        style={{
          width: '34px',
          height: '34px',
          animation: 'upamanPulse 1.4s ease-in-out infinite'
        }}
      />
      <div style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        border: `3px solid ${isDarkMode ? '#35516d' : '#cbd5e1'}`,
        borderTopColor: '#0f766e',
        animation: 'upamanSpinner 0.9s linear infinite'
      }} />
      <div style={{
        color: isDarkMode ? '#cbd5e1' : '#334155',
        fontSize: '0.95rem',
        fontWeight: 600
      }}>
        Loading calculator...
      </div>
    </div>
  </div>
);

const getIsDarkTheme = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('theme') === 'dark';
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(getIsDarkTheme);

  useEffect(() => {
    const syncTheme = () => setIsDarkMode(getIsDarkTheme());

    window.addEventListener('storage', syncTheme);
    window.addEventListener('upaman-theme-change', syncTheme);

    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('upaman-theme-change', syncTheme);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen">
          <Suspense fallback={<LoadingSpinner isDarkMode={isDarkMode} />}>
            <Routes>
              <Route path="/irctc-calculator" element={<IRCTCCalculator />} />
              <Route path="/uk-rail-calculator" element={<UKRailCalculator isDarkMode={isDarkMode} />} />
              <Route path="/credit-card-analyzer" element={<CreditCardAnalyzer />} />
              <Route path="/ppf-calculator" element={<PPFCalculator />} />
              <Route path="/credit-card-trap-calculator" element={<CreditCardTrapCalculator />} />
              <Route path="/loan-calculator" element={<ComprehensiveLoanCalculator />} />
              <Route path="/sip-calculator" element={<SIPCalculator />} />
              <Route path="/gst-calculator" element={<GSTCalculator />} />
              <Route path="/income-tax-calculator" element={<IncomeTaxCalculator />} />
              <Route path="/salary-calculator" element={<SalaryCalculator />} />
              <Route path="/json-tools" element={<JsonTools />} />
              <Route path="/india-calculators" element={<IndiaCalculatorsHub />} />
              <Route path="/us-calculators" element={<USCalculatorsHub />} />
              <Route path="/eu-calculators" element={<EUCalculatorsHub />} />
              <Route path="/us-mortgage-calculator" element={<USMortgageCalculator />} />
              <Route path="/us-refinance-calculator" element={<USRefinanceCalculator />} />
              <Route path="/us-auto-loan-calculator" element={<USAutoLoanCalculator />} />
              <Route path="/us-401k-calculator" element={<US401kCalculator />} />
              <Route path="/us-savings-cd-calculator" element={<USSavingsCDCalculator />} />
              <Route path="/us-credit-card-payoff-calculator" element={<USCreditCardPayoffCalculator />} />
              <Route path="/job-offer-workflow" element={<JobOfferWorkflow />} />
              <Route path="/home-loan-readiness-workflow" element={<HomeLoanReadinessWorkflow />} />
              <Route path="/age-calculator" element={<AgeCalculator />} />
              
              {/* European Calculators */}
              <Route path="/uk-income-tax-calculator" element={<UKIncomeTaxCalculator />} />
              <Route path="/eu-vat-calculator" element={<VATCalculator />} />
              <Route path="/european-salary-calculator" element={<EuropeanSalaryCalculator />} />
              <Route path="/germany-salary-calculator" element={<GermanySalaryCalculator />} />
              <Route path="/france-salary-calculator" element={<FranceSalaryCalculator />} />
              <Route path="/netherlands-salary-calculator" element={<NetherlandsSalaryCalculator />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="/" element={<MainLandingPage/>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <ConditionalFooter />
        </div>
      </Router>
    </HelmetProvider>
  );
}



export default App;
